import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import Header from './Header.jsx'
import Footer from './Footer.jsx'
import AdUnit from './AdUnit.jsx'
import { supabase } from '../lib/supabase'
import { MAX_YEAR } from './constants.js'
import DocumentMeta from './DocumentMeta.jsx'

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts'

function formatScore(n) {
  if (n === null || n === undefined) return '-'
  return Number(n).toLocaleString('el-GR')
}

function formatCoeff(n) {
  if (n === null || n === undefined) return '-'
  return Number(n).toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const OPTIONAL_SUBJECT_LABELS = {
  english: 'Αγγλική Γλώσσα',
  french: 'Γαλλική Γλώσσα',
  german: 'Γερμανική Γλώσσα',
  italian: 'Ιταλική Γλώσσα',
  spanish: 'Ισπανική Γλώσσα',
  free_design: 'Ελεύθερο Σχέδιο',
  linear_design: 'Γραμμικό Σχέδιο',
  freehand_design: 'Ελεύθερο Σχέδιο',
  freehand_drawing: 'Ελεύθερο Σχέδιο',
  linear_drawing: 'Γραμμικό Σχέδιο',
  harmony: 'Αρμονία',
  music: 'Μουσική',
  athletics: 'Αγωνίσματα',
  foreign_language: 'Ξένη Γλώσσα',
}

function subjectLabel(code) {
  return OPTIONAL_SUBJECT_LABELS[code] || code
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const value = payload?.[0]?.value
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-md">
      <div className="text-xs font-semibold text-gray-700">Έτος: {label}</div>
      <div className="mt-1 text-sm font-extrabold text-gray-900">
        {formatScore(value)} <span className="text-xs font-semibold text-gray-500">μόρια</span>
      </div>
    </div>
  )
}

function DepartmentDetailsPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const backTarget = location.state?.from

  const [loading, setLoading] = useState(true)
  const [deptLoading, setDeptLoading] = useState(true)
  const [scoresLoading, setScoresLoading] = useState(true)
  const [metaLoading, setMetaLoading] = useState(true)
  const [error, setError] = useState(null)

  const [department, setDepartment] = useState(null)
  const [scores, setScores] = useState([])
  const [yearStats, setYearStats] = useState([])

  useEffect(() => {
    let cancelled = false

    async function run() {
      setLoading(true)
      setDeptLoading(true)
      setScoresLoading(true)
      setMetaLoading(true)
      setError(null)

      try {
        const { data: dept, error: deptErr } = await supabase
          .from('departments')
          .select('id, name, university, city, slug, optional_subjects, optional_subjects_raw')
          .eq('slug', slug)
          .maybeSingle()

        if (deptErr) throw deptErr

        if (!dept) {
          if (!cancelled) {
            setDepartment(null)
            setScores([])
            setYearStats([])
          }
          return
        }

        if (!cancelled) {
          setDepartment(dept)
          setDeptLoading(false)
        }

        const { data: sc, error: scErr } = await supabase
          .from('scores')
          .select('year, score')
          .eq('department_id', dept.id)
          .order('year', { ascending: true })

        if (scErr) throw scErr

        const normalized = (sc || [])
          .filter((x) => typeof x.year === 'number' && typeof x.score === 'number')
          .map((x) => ({ year: x.year, score: x.score }))

        const { data: ys, error: ysErr } = await supabase
          .from('department_year_stats')
          .select('year, ebe_department_coeff, ebe_special_coeff, entrants_total')
          .eq('department_id', dept.id)
          .order('year', { ascending: true })

        if (ysErr) throw ysErr

        const normalizedYearStats = (ys || [])
          .filter((x) => typeof x.year === 'number')
          .map((x) => ({
            year: x.year,
            ebe_department_coeff: x.ebe_department_coeff === null ? null : Number(x.ebe_department_coeff),
            ebe_special_coeff: x.ebe_special_coeff === null ? null : Number(x.ebe_special_coeff),
            entrants_total: x.entrants_total === null ? null : Number(x.entrants_total),
          }))

        if (!cancelled) {
          setScores(normalized)
          setYearStats(normalizedYearStats)
          setScoresLoading(false)
          setMetaLoading(false)
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Κάτι πήγε στραβά.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [slug])

  const stats = useMemo(() => {
    if (!scores?.length) return null
    const values = scores.map((s) => s.score)
    const min = Math.min(...values)
    const max = Math.max(...values)
    const avg = Math.round(values.reduce((a, b) => a + b, 0) / values.length)
    const last = scores[scores.length - 1]
    const prev = scores.length >= 2 ? scores[scores.length - 2] : null
    const yoy = prev ? last.score - prev.score : null
    return { min, max, avg, last, prev, yoy }
  }, [scores])

  const deptName = department?.name || 'Τμήμα'
  const uniName = department?.university || ''
  const cityName = department?.city || ''

  const seoTitle = department
    ? `Βάσεις ${deptName} ${uniName}${cityName ? ` (${cityName})` : ''} ${MAX_YEAR} | Πανελλήνιες+`
    : `Τμήμα | Πανελλήνιες+`

  const seoDesc = department
    ? `Βάσεις (μόρια) για ${deptName} ${uniName}${cityName ? `, ${cityName}` : ''}. Δες εξέλιξη βάσης ανά έτος έως ${MAX_YEAR}, ελάχιστη/μέγιστη/μέσο όρο και διαφορά από πέρσι.`
    : `Δες βάσεις και στοιχεία τμήματος.`


  const yoyLabel = useMemo(() => {
    if (stats?.yoy === null || stats?.yoy === undefined) return '—'
    const sign = stats.yoy > 0 ? '+' : ''
    return `${sign}${formatScore(stats.yoy)}`
  }, [stats])

  const optionalSubjects = useMemo(() => {
    if (!Array.isArray(department?.optional_subjects)) return []
    return department.optional_subjects.filter(Boolean)
  }, [department])

  const hasOptionalSubjectRequirement = useMemo(
    () => optionalSubjects.length > 0 || Boolean(department?.optional_subjects_raw?.trim()),
    [optionalSubjects, department?.optional_subjects_raw]
  )

  const ebeRows = useMemo(
    () =>
      (yearStats || [])
        .filter((x) => x.ebe_department_coeff !== null || x.ebe_special_coeff !== null)
        .sort((a, b) => a.year - b.year),
    [yearStats]
  )

  const entrantsRows = useMemo(
    () =>
      (yearStats || [])
        .filter((x) => Number.isFinite(x.entrants_total))
        .sort((a, b) => a.year - b.year),
    [yearStats]
  )

  return (
    <>
    <DocumentMeta
      title={seoTitle}
      description={seoDesc}
      canonical={`https://www.panellhniesplus.gr/tmima/${department?.slug || slug}`}
    />
    {department && (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Βάσεις",
                item: "https://www.panellhniesplus.gr/vaseis",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: deptName,
                item: `https://www.panellhniesplus.gr/tmima/${department?.slug || slug}`,
              },
            ],
          }),
        }}
      />
    )}
    <div className="min-h-screen bg-white text-gray-900">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Top header row */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <button
                onClick={() => (backTarget ? navigate(backTarget) : navigate(-1))}
                className="cursor-pointer inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                ← Πίσω
              </button>

              <span className="text-xs text-gray-500">
                Τελευταία ενημέρωση βάσεων: {MAX_YEAR}
              </span>
            </div>

            <h1 className="mt-4 text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
              {loading ? 'Φόρτωση...' : deptName}
            </h1>

            {uniName ? (
              <p className="mt-2 text-sm sm:text-base text-gray-600">
                {uniName}
                {department?.city ? <span className="text-gray-500"> • {department.city}</span> : null}
              </p>
            ) : null}
          </div>

          {/* Removed the top-right stat chips */}
        </div>

        {error ? (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
            {error}
          </div>
        ) : null}

        {!loading && !department ? (
          <div className="mt-8 rounded-2xl border border-gray-200 bg-gray-50 p-6">
            <h2 className="text-lg font-extrabold text-gray-900">Δεν βρέθηκε τμήμα</h2>
            <p className="mt-2 text-sm text-gray-600">
              Το τμήμα που ζήτησες δεν υπάρχει (ή δεν είναι διαθέσιμο).
            </p>
            <button
              onClick={() => (backTarget ? navigate(backTarget) : navigate(`/vaseis/${MAX_YEAR}?ordering=-score`, { replace: true }))}
              className="cursor-pointer mt-4 inline-flex items-center justify-center bg-gray-900 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-800 transition"
            >
              Πίσω στις βάσεις
            </button>
          </div>
        ) : null}

        {/* Chart card */}
        {department ? (
          <section className="mt-6 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-extrabold text-gray-900">Εξέλιξη βάσης ανά έτος</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Δες πώς διαμορφώθηκαν τα μόρια εισαγωγής του τμήματος τα τελευταία χρόνια.
                </p>
              </div>

              {/* <button
                onClick={() => (backTarget ? navigate(backTarget) : navigate('/vaseis'))}
                className="cursor-pointer inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 transition"
              >
                Αναζήτηση άλλου τμήματος
              </button> */}
            </div>

            <div className="p-4 sm:p-6">
              <div className="h-[260px] sm:h-[340px]">
                {deptLoading || scoresLoading ? (
                  <div className="flex h-full items-center justify-center text-gray-500">
                    Φόρτωση γραφήματος...
                  </div>
                ) : scores.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center text-center">
                    <div className="text-gray-900 font-extrabold">Δεν υπάρχουν διαθέσιμα δεδομένα</div>
                    <div className="mt-1 text-sm text-gray-600">
                      Δεν βρέθηκαν βάσεις για αυτό το τμήμα.
                    </div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={scores} margin={{ top: 10, right: 12, left: 18, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="4 4" />
                      <XAxis dataKey="year" tickMargin={10} />
                      <YAxis tickMargin={10} width={74} tickFormatter={formatScore} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line type="monotone" dataKey="score" stroke="#111827" strokeWidth={3} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </section>
        ) : null}

        {/* In-content ad — after the chart, a natural reading break */}
        {department ? <AdUnit /> : null}

        {/* Extra info area (moved stats here) */}
        {department ? (
          <section className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Data summary */}
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-2xl shadow-sm p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-extrabold text-gray-900">Γρήγορη εικόνα</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Συνοπτικά στοιχεία για τις βάσεις του τμήματος.
                  </p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs font-semibold text-gray-500">Τελευταίο έτος</div>
                  <div className="mt-1 text-lg font-extrabold text-gray-900">
                    {stats ? `${stats.last.year}` : '—'}
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs font-semibold text-gray-500">Βάση ({stats?.last?.year ?? '—'})</div>
                  <div className="mt-1 text-lg font-extrabold text-gray-900">
                    {stats ? formatScore(stats.last.score) : '—'}
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs font-semibold text-gray-500">Διαφορά από πέρσι</div>
                  <div className="mt-1 text-lg font-extrabold text-gray-900">
                    {scores?.length >= 2 ? yoyLabel : '—'}
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs font-semibold text-gray-500">Μέσος όρος</div>
                  <div className="mt-1 text-lg font-extrabold text-gray-900">
                    {stats ? formatScore(stats.avg) : '—'}
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs font-semibold text-gray-500">Ελάχιστη</div>
                  <div className="mt-1 text-lg font-extrabold text-gray-900">
                    {stats ? formatScore(stats.min) : '—'}
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-xs font-semibold text-gray-500">Μέγιστη</div>
                  <div className="mt-1 text-lg font-extrabold text-gray-900">
                    {stats ? formatScore(stats.max) : '—'}
                  </div>
                </div>
              </div>
            </div>

            {/* Details card */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 sm:p-6">
              <h3 className="text-base font-extrabold text-gray-900">Στοιχεία</h3>

              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-500 font-semibold">Πόλη</span>
                  <span className="text-gray-900 font-extrabold text-right">{department.city || '—'}</span>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-500 font-semibold">Πανεπιστήμιο</span>
                  <span className="text-gray-900 font-extrabold text-right">{department.university || '—'}</span>
                </div>
              </div>
            </div>
          </section>
        ) : null}

        {department && (optionalSubjects.length > 0 || department.optional_subjects_raw) ? (
          <section className="mt-6 bg-white border border-gray-200 rounded-2xl shadow-sm p-5 sm:p-6">
            <h3 className="text-base font-extrabold text-gray-900">Απαιτούμενα Ειδικά Μαθήματα</h3>
            {optionalSubjects.length > 0 ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {optionalSubjects.map((code) => (
                  <span
                    key={code}
                    className="inline-flex items-center rounded-full bg-gray-900 px-3 py-1.5 text-xs font-bold text-white"
                  >
                    {subjectLabel(code)}
                  </span>
                ))}
              </div>
            ) : null}
            {department.optional_subjects_raw ? (
              <p className="mt-4 text-sm text-gray-600">{department.optional_subjects_raw}</p>
            ) : null}
          </section>
        ) : null}

        {department && !metaLoading && ebeRows.length > 0 ? (
          <section className="mt-6 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 sm:p-6 border-b border-gray-100">
              <h3 className="text-base font-extrabold text-gray-900">Συντελεστές ΕΒΕ ανά έτος</h3>
            </div>
            <div className="p-4 sm:p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-600">
                      <th className="py-2 text-left font-semibold">Έτος</th>
                      <th className="py-2 text-left font-semibold">Τμήματος</th>
                      {hasOptionalSubjectRequirement ? (
                        <th className="py-2 text-left font-semibold">Ειδικού μαθήματος</th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {[...ebeRows].reverse().map((row) => (
                      <tr key={row.year} className="border-b border-gray-100">
                        <td className="py-2 font-semibold text-gray-900">{row.year}</td>
                        <td className="py-2 text-gray-700">{formatCoeff(row.ebe_department_coeff)}</td>
                        {hasOptionalSubjectRequirement ? (
                          <td className="py-2 text-gray-700">{formatCoeff(row.ebe_special_coeff)}</td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        ) : null}

        {department && !metaLoading && entrantsRows.length > 0 ? (
          <section className="mt-6 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 sm:p-6 border-b border-gray-100">
              <h3 className="text-base font-extrabold text-gray-900">Πλήθος εισαχθέντων ανά έτος</h3>
            </div>
            <div className="p-4 sm:p-6">
              <div className="h-[220px] sm:h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={entrantsRows} margin={{ top: 10, right: 12, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="4 4" />
                    <XAxis dataKey="year" tickMargin={10} />
                    <YAxis tickMargin={10} width={60} />
                    <Tooltip />
                    <Line type="monotone" dataKey="entrants_total" name="Εισαχθέντες" stroke="#16a34a" strokeWidth={3} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-600">
                      <th className="py-2 text-left font-semibold">Έτος</th>
                      <th className="py-2 text-left font-semibold">Εισαχθέντες</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...entrantsRows].reverse().map((row) => (
                      <tr key={row.year} className="border-b border-gray-100">
                        <td className="py-2 font-semibold text-gray-900">{row.year}</td>
                        <td className="py-2 text-gray-700">{formatScore(row.entrants_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        ) : null}
      </main>

      <Footer />
    </div>
    </>
  )
}

export default DepartmentDetailsPage
